import { z } from 'zod';

import {
  createFormSubmissionRequestSchema,
  createFormTemplateRequestSchema,
  formSubmissionEnvelopeSchema,
  formSubmissionListEnvelopeSchema,
  formTemplateEnvelopeSchema,
  formTemplateListEnvelopeSchema,
  type CreateFormSubmissionRequest,
  type CreateFormTemplateRequest,
  type FormSubmissionResponse,
  type FormTemplateResponse,
} from '@emr/shared';

import { apiRequest } from './client.ts';

/** Both DELETE routes in this module (`archiveFormTemplate`, `deleteFormSubmission`) return `204
 * No Content` on success — an empty body, which `client.ts`'s `apiRequest` parses to `undefined`
 * before handing it to the schema below. `z.undefined()` is the schema for that, not the resource
 * envelope: there is no resource left to parse a response body into. */
const noContentSchema = z.undefined();

/**
 * The form-template builder's API surface (`BACKLOG.md` C10b), validated against `@emr/shared`'s own
 * request/response schemas — matching `api/rcm.ts`'s own stated reasoning for why this cannot
 * silently drift from what the server actually accepts and returns.
 */

export type {
  CreateFormSubmissionRequest,
  CreateFormTemplateRequest,
  FormSubmissionResponse,
  FormTemplateResponse,
};

export async function fetchFormTemplates(
  signal?: AbortSignal,
): Promise<readonly FormTemplateResponse[]> {
  const response = await apiRequest(
    '/api/form-templates',
    formTemplateListEnvelopeSchema,
    signal === undefined ? {} : { signal },
  );
  return response.templates;
}

export async function fetchFormTemplate(
  templateId: string,
  signal?: AbortSignal,
): Promise<FormTemplateResponse> {
  const response = await apiRequest(
    `/api/form-templates/${templateId}`,
    formTemplateEnvelopeSchema,
    signal === undefined ? {} : { signal },
  );
  return response.template;
}

export async function createFormTemplate(
  input: CreateFormTemplateRequest,
): Promise<FormTemplateResponse> {
  createFormTemplateRequestSchema.parse(input);
  const response = await apiRequest('/api/form-templates', formTemplateEnvelopeSchema, {
    method: 'POST',
    body: input,
  });
  return response.template;
}

export async function publishFormTemplateVersion(
  templateFamilyId: string,
  input: CreateFormTemplateRequest,
): Promise<FormTemplateResponse> {
  createFormTemplateRequestSchema.parse(input);
  const response = await apiRequest(
    `/api/form-templates/${templateFamilyId}/versions`,
    formTemplateEnvelopeSchema,
    { method: 'POST', body: input },
  );
  return response.template;
}

export async function archiveFormTemplate(templateFamilyId: string): Promise<void> {
  await apiRequest(`/api/form-templates/${templateFamilyId}`, noContentSchema, {
    method: 'DELETE',
  });
}

function submissionsPath(patientId: string, suffix = ''): string {
  return `/api/patients/${patientId}/forms/submissions${suffix}`;
}

export async function fetchFormSubmissions(
  patientId: string,
  signal?: AbortSignal,
): Promise<readonly FormSubmissionResponse[]> {
  const response = await apiRequest(
    submissionsPath(patientId),
    formSubmissionListEnvelopeSchema,
    signal === undefined ? {} : { signal },
  );
  return response.submissions;
}

export async function createFormSubmission(
  patientId: string,
  input: CreateFormSubmissionRequest,
): Promise<FormSubmissionResponse> {
  createFormSubmissionRequestSchema.parse(input);
  const response = await apiRequest(submissionsPath(patientId), formSubmissionEnvelopeSchema, {
    method: 'POST',
    body: input,
  });
  return response.submission;
}

export async function deleteFormSubmission(patientId: string, submissionId: string): Promise<void> {
  await apiRequest(submissionsPath(patientId, `/${submissionId}`), noContentSchema, {
    method: 'DELETE',
  });
}
